import { useCallback, useEffect, useState } from "react";
import type { Difficulty, GameState, Pace, Rations } from "@/game/types";
import {
  advance,
  applyMinigameReward,
  beginOutfitting,
  createInitialState,
  leaveLandmark,
  resolveEventChoice,
  setPace as setPaceFn,
  setRations as setRationsFn,
  startGame as startGameFn,
  trade as tradeFn,
} from "@/game/engine";

const STORAGE_KEY = "bch-trail:save:v1";
// Previous key, kept so in-progress games carry over after the rename.
const LEGACY_STORAGE_KEY = "satoshi-trail:save:v1";

function loadState(): GameState {
  try {
    const raw =
      localStorage.getItem(STORAGE_KEY) ?? localStorage.getItem(LEGACY_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as GameState;
      // basic sanity check
      if (parsed && typeof parsed.day === "number" && Array.isArray(parsed.party)) {
        // backfill / sanitize fields added or changed in later versions
        const validWeather = ["clear", "rain", "storm", "snow"];
        const weather = validWeather.includes(parsed.weather as string)
          ? parsed.weather
          : "clear";
        const validDifficulty = ["banker", "pleb", "no-coiner"];
        const difficulty = validDifficulty.includes(parsed.difficulty as string)
          ? parsed.difficulty
          : "pleb";
        const merged: GameState = {
          ...createInitialState(),
          ...parsed,
          weather,
          difficulty,
          // Older saves stored the full event object (now just an id).
          activeEventId: parsed.activeEventId ?? null,
        };
        // If we were mid-event but the id didn't survive, drop back to traveling
        // so the player is never stuck on an empty event screen.
        if (merged.phase === "event" && !merged.activeEventId) {
          merged.phase = "traveling";
        }
        return merged;
      }
    }
  } catch {
    // ignore
  }
  return createInitialState();
}

export function useGame() {
  const [state, setState] = useState<GameState>(() => loadState());

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // ignore quota / serialization errors
    }
  }, [state]);

  const start = useCallback((names: string[], difficulty?: Difficulty) => {
    setState(startGameFn(names, difficulty));
  }, []);

  const beginJourney = useCallback(() => {
    setState((s) => beginOutfitting(s));
  }, []);

  const travel = useCallback(() => {
    setState((s) => advance(s));
  }, []);

  const chooseEvent = useCallback((index: number) => {
    setState((s) => resolveEventChoice(s, index));
  }, []);

  const setPace = useCallback((pace: Pace) => {
    setState((s) => setPaceFn(s, pace));
  }, []);

  const setRations = useCallback((rations: Rations) => {
    setState((s) => setRationsFn(s, rations));
  }, []);

  const continueFromLandmark = useCallback(() => {
    setState((s) => leaveLandmark(s));
  }, []);

  const trade = useCallback(
    (item: "food" | "medicine" | "gear", qty: number, unitPrice: number) => {
      setState((s) => tradeFn(s, item, qty, unitPrice));
    },
    [],
  );

  const openMinigame = useCallback(() => {
    setState((s) => ({ ...s, phase: "minigame" }));
  }, []);

  const finishMinigame = useCallback((sats: number) => {
    setState((s) => applyMinigameReward(s, sats));
  }, []);

  const openTrading = useCallback(() => {
    setState((s) => ({ ...s, phase: "trading" }));
  }, []);

  const closeTrading = useCallback(() => {
    setState((s) => ({ ...s, phase: "landmark" }));
  }, []);

  const reset = useCallback(() => {
    setState(createInitialState());
  }, []);

  return {
    state,
    start,
    beginJourney,
    travel,
    chooseEvent,
    setPace,
    setRations,
    continueFromLandmark,
    trade,
    openMinigame,
    finishMinigame,
    openTrading,
    closeTrading,
    reset,
  };
}
